/**
 * configurator-viewer.js
 * Three.js viewer, string art shader, and 3D model builder for the ThreadTheme product configurator.
 * Adapted from DOCS/example/scripts/ for Shopify asset URL injection via config._assetUrls.
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js';
//import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { applyStringArtFilter } from './string-art-filter.js';
import { buildBWGenerator }     from './string-art-bw.js';

// ---------------------------------------------------------------------------
// ColoredArtProcessor — wraps applyStringArtFilter for circular boards
// ---------------------------------------------------------------------------

class ColoredArtProcessor {
  /**
   * Render the colored string-art synchronously to a canvas.
   *
   * @param {HTMLCanvasElement|HTMLImageElement} source - Cropped source image
   * @param {object} params - Merged params from sizeConfig.colored_art
   * @returns {HTMLCanvasElement} offscreen canvas with the rendered art
   */
  process(source, params = {}) {
    const canvas = document.createElement('canvas');
    applyStringArtFilter(source, canvas, {
      shape:        'circle',
      size:         params.size    ?? 900,
      totalLines:   params.totalLines   ?? 5500,
      lineAlpha:    params.lineAlpha    ?? 0.19,
      lineWidth:    params.lineWidth    ?? 0.50,
      bgBlend:      params.bgBlend     ?? 0.44,
      numNails:     params.numNails     ?? 360,
      darkBias:     params.darkBias     ?? 0.45,
      uniformRatio: params.uniformRatio ?? 0.15,
      saturation:   params.saturation   ?? 1.85,
      contrast:     params.contrast     ?? 1.0,
    });
    return canvas;
  }
}

// ---------------------------------------------------------------------------
// BWArtProcessor — wraps buildBWGenerator for circular boards with live updates
// ---------------------------------------------------------------------------

class BWArtProcessor {
  constructor() {
    this._generator  = null;
    this._rafId      = null;
    this._texture    = null;
    this._canvas     = null;
    this._stepsPerFrame = 15;
  }

  /** Stop any running generation. */
  stop() {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._generator = null;
  }

  /** Swap to a new texture (e.g. after a board-shape rebuild). Generator keeps running. */
  setTexture(texture) {
    this._texture = texture;
  }

  /**
   * Start iterative generation.
   * Renders into `canvas`, marks `texture.needsUpdate` after each batch.
   *
   * @param {HTMLCanvasElement|HTMLImageElement} source
   * @param {object}            params  - sizeConfig.bw_art fields
   * @param {HTMLCanvasElement} canvas  - canvas to render into (already in scene)
   * @param {THREE.CanvasTexture} texture - texture to ping as needsUpdate
   */
  async start(source, params, canvas, texture) {
    this.stop();

    this._canvas  = canvas;
    this._texture = texture;

    const cfg = {
      nailCount:   params.nailCount   ?? 240,
      maxLines:    params.maxLines    ?? 4000,
      lineAlpha:   params.lineAlpha   ?? 22,
      minAngleDeg: params.minAngleDeg ?? 30,
      workSize:    Math.max(canvas.width, 500),
    };

    let gen;
    try {
      gen = await buildBWGenerator(source, cfg);
    } catch (err) {
      console.error('[BWArtProcessor] buildBWGenerator failed:', err);
      return;
    }

    this._generator = gen;

    // Initial render
    gen.render(canvas);
    texture.needsUpdate = true;

    const step = () => {
      if (!this._generator || this._generator !== gen) return;

      const more = gen.runSteps(this._stepsPerFrame);
      gen.render(canvas);
      texture.needsUpdate = true;

      if (more) {
        this._rafId = requestAnimationFrame(step);
      } else {
        this._rafId = null;
        console.log('[BWArtProcessor] Generation complete');
      }
    };

    this._rafId = requestAnimationFrame(step);
  }
}

// ---------------------------------------------------------------------------
// StringArtShader
// ---------------------------------------------------------------------------

export class StringArtShader {
  constructor(config) {
    this.config = config;
    this.gl = null;
    this.program = null;
    this.patchTextures = [];
    this.canvas = null;
    this.sourceTexture = null;
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) return;

    // Create offscreen canvas for WebGL
    this.canvas = document.createElement('canvas');
    this.gl =
      this.canvas.getContext('webgl') ||
      this.canvas.getContext('experimental-webgl');

    if (!this.gl) {
      throw new Error('WebGL not supported');
    }

    // Load patch textures from Shopify CDN URLs
    const assetUrls = this.config._assetUrls;
    const numPatches = this.config.string_art_shader.num_patches;
    const patchPromises = [];
    for (let i = 0; i < numPatches; i++) {
      const url = assetUrls.patches[i];
      patchPromises.push(this.loadTexture(url));
    }
    this.patchTextures = await Promise.all(patchPromises);

    // Compile shaders
    await this.compileShaders();

    this.initialized = true;
  }

  loadTexture(url) {
    return new Promise((resolve, reject) => {
      const gl = this.gl;
      const texture = gl.createTexture();
      const image = new Image();
      image.crossOrigin = 'anonymous';

      image.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          image,
        );

        if (this.isPowerOf2(image.width) && this.isPowerOf2(image.height)) {
          gl.generateMipmap(gl.TEXTURE_2D);
        } else {
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        }

        resolve(texture);
      };

      image.onerror = () => reject(new Error(`Failed to load texture: ${url}`));
      image.src = url;
    });
  }

  isPowerOf2(value) {
    return (value & (value - 1)) === 0;
  }

  async compileShaders() {
    const gl = this.gl;

    const vertexShaderSource = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
#else
  precision mediump float;
#endif
attribute vec2 vertexPositionNDC;
varying vec2 vTexCoords;
void main() {
    vTexCoords = vertexPositionNDC * 0.5 + vec2(0.5);
    gl_Position = vec4(vertexPositionNDC, 0.0, 1.0);
}
    `;

    const fragmentShaderSource = `
precision mediump float;
uniform sampler2D patch0;
uniform sampler2D patch1;
uniform sampler2D patch2;
uniform sampler2D patch3;
uniform sampler2D patch4;
uniform sampler2D patch5;
uniform sampler2D patch6;
uniform sampler2D ref_img;
uniform vec2 tile_scale;
uniform vec2 Tinvsize;
uniform float gamma;
uniform vec2 hist_clip;
varying vec2 vTexCoords;
#define NUM_PATCHES 8
float p_w[NUM_PATCHES];
float get_octave(int k_id, float ref, float patch, float pmin1, float p, float pplus1) {
    float delta = ref - p;
    float weight = 1.0;
    if (k_id < NUM_PATCHES - 1) weight -= max(0.0, delta) / (pplus1 - p);
    if (k_id > 0) weight -= max(0.0, -delta) / (p - pmin1);
    weight = clamp(weight, 0.0, 1.0);
    return weight * patch;
}
float luma(vec4 color) { return dot(color.rgb, vec3(0.299, 0.587, 0.114)); }
float get_median5x5(vec2 uv) {
    #define s2(a, b) temp = a; a = min(a, b); b = max(temp, b);
    #define t2(a, b) s2(v[a], v[b]);
    #define t24(a, b, c, d, e, f, g, h) t2(a, b); t2(c, d); t2(e, f); t2(g, h);
    #define t25(a, b, c, d, e, f, g, h, i, j) t24(a, b, c, d, e, f, g, h); t2(i, j);
    float v[25];
    for(int dX = -2; dX <= 2; ++dX) {
        for(int dY = -2; dY <= 2; ++dY) {
            vec2 offset = vec2(float(dX), float(dY));
            v[(dX + 2) * 5 + (dY + 2)] = luma(texture2D(ref_img, uv + offset * Tinvsize));
        }
    }
    float temp;
    t25(0, 1,       3, 4,       2, 4,       2, 3,       6, 7);
    t25(5, 7,       5, 6,       9, 7,       1, 7,       1, 4);
    t25(12, 13,     11, 13,     11, 12,     15, 16,     14, 16);
    t25(14, 15,     18, 19,     17, 19,     17, 18,     21, 22);
    t25(20, 22,     20, 21,     23, 24,     2, 5,       3, 6);
    t25(0, 6,       0, 3,       4, 7,       1, 7,       1, 4);
    t25(11, 14,     8, 14,      8, 11,      12, 15,     9, 15);
    t25(9, 12,      13, 16,     10, 16,     10, 13,     20, 23);
    t25(17, 23,     17, 20,     21, 24,     18, 24,     18, 21);
    t25(19, 22,     8, 17,      9, 18,      0, 18,      0, 9);
    t25(10, 19,     1, 19,      1, 10,      11, 20,     2, 20);
    t25(2, 11,      12, 21,     3, 21,      3, 12,      13, 22);
    t25(4, 22,      4, 13,      14, 23,     5, 23,      5, 14);
    t25(15, 24,     6, 24,      6, 15,      7, 16,      7, 19);
    t25(3, 11,      5, 17,      11, 17,     9, 17,      4, 10);
    t25(6, 12,      7, 14,      4, 6,       4, 7,       12, 14);
    t25(10, 14,     6, 7,       10, 12,     6, 10,      6, 17);
    t25(12, 17,     7, 17,      7, 10,      12, 18,     7, 12);
    t24(10, 18,     12, 20,     10, 20,     10, 12);
    return v[12];
}
void main() {
    p_w[0] = 0.000000;
    p_w[1] = 0.250980;
    p_w[2] = 0.501961;
    p_w[3] = 0.800000;
    p_w[4] = 0.901961;
    p_w[5] = 0.980392;
    p_w[6] = 1.000000;
    vec2 ref_uvs = vec2(vTexCoords.x, 1.0 - vTexCoords.y);
    float in_median = get_median5x5(ref_uvs);
    float in_median_remap = in_median * 255.0 / 254.0;
    float ref = clamp((in_median_remap - hist_clip.x) / (hist_clip.y - hist_clip.x), 0.0, 1.0);
    ref = pow(ref, gamma);
    float result = 0.0;
    result += get_octave(0, ref, luma(texture2D(patch0, tile_scale * vTexCoords)), p_w[0], p_w[0], p_w[1]);
    result += get_octave(1, ref, luma(texture2D(patch1, tile_scale * vTexCoords)), p_w[0], p_w[1], p_w[2]);
    result += get_octave(2, ref, luma(texture2D(patch2, tile_scale * vTexCoords)), p_w[1], p_w[2], p_w[3]);
    result += get_octave(3, ref, luma(texture2D(patch3, tile_scale * vTexCoords)), p_w[2], p_w[3], p_w[4]);
    result += get_octave(4, ref, luma(texture2D(patch4, tile_scale * vTexCoords)), p_w[3], p_w[4], p_w[5]);
    result += get_octave(5, ref, luma(texture2D(patch5, tile_scale * vTexCoords)), p_w[4], p_w[5], p_w[6]);
    result += get_octave(6, ref, luma(texture2D(patch6, tile_scale * vTexCoords)), p_w[5], p_w[6], p_w[7]);
    result = in_median == 1.0 ? 1.0 : result;
    const float TINT = 0.98;
    result *= TINT;
    gl_FragColor = vec4(result, result, result, 1.0);
}
    `;

    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);

    if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
      throw new Error(
        'Vertex shader compilation failed: ' + gl.getShaderInfoLog(vertexShader),
      );
    }

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);

    if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
      throw new Error(
        'Fragment shader compilation failed: ' + gl.getShaderInfoLog(fragmentShader),
      );
    }

    this.program = gl.createProgram();
    gl.attachShader(this.program, vertexShader);
    gl.attachShader(this.program, fragmentShader);
    gl.linkProgram(this.program);

    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      throw new Error(
        'Shader program linking failed: ' + gl.getProgramInfoLog(this.program),
      );
    }

    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    // Create screen quad
    const vertices = new Float32Array([
      -1.0, -1.0, 1.0, -1.0, -1.0, 1.0, -1.0, 1.0, 1.0, -1.0, 1.0, 1.0,
    ]);

    this.screenQuadVBO = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.screenQuadVBO);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
  }

  /**
   * Process source canvas through the string art WebGL shader.
   * @param {HTMLCanvasElement} sourceCanvas
   * @param {number} nailAreaWidthCm  - nail area width in centimetres
   * @param {number} nailAreaHeightCm - nail area height in centimetres
   * @returns {HTMLCanvasElement} rendered output canvas
   */
  processImage(sourceCanvas, nailAreaWidthCm, nailAreaHeightCm) {
    if (!this.initialized) {
      throw new Error('Shader not initialized. Call initialize() first.');
    }

    const gl = this.gl;
    const shaderConfig = this.config.string_art_shader;

    // Size canvas for processing (config uses cm-based px density)
    const processingWidth = Math.round(nailAreaWidthCm * shaderConfig.src_px_per_cm);
    const processingHeight = Math.round(nailAreaHeightCm * shaderConfig.src_px_per_cm);

    this.canvas.width = processingWidth;
    this.canvas.height = processingHeight;

    // Create temporary canvas for source pre-processing
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = processingWidth;
    tempCanvas.height = processingHeight;
    const ctx = tempCanvas.getContext('2d');

    ctx.drawImage(sourceCanvas, 0, 0, tempCanvas.width, tempCanvas.height);

    // Pre-process: clamp non-transparent pixels to max 254, make transparent white
    const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      if (alpha <= 127) {
        // Transparent pixel — white means no strings
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
        data[i + 3] = 255;
      } else {
        // Non-transparent — clamp brightness to 254 so shader can distinguish from white
        if (data[i] === 255) data[i] = 254;
        if (data[i + 1] === 255) data[i + 1] = 254;
        if (data[i + 2] === 255) data[i + 2] = 254;
        data[i + 3] = 255;
      }
    }
    ctx.putImageData(imageData, 0, 0);

    // Upload source image as WebGL texture
    if (!this.sourceTexture) {
      this.sourceTexture = gl.createTexture();
    }
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, tempCanvas);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

    // Render
    gl.viewport(0, 0, processingWidth, processingHeight);
    gl.useProgram(this.program);

    // Bind screen quad
    gl.bindBuffer(gl.ARRAY_BUFFER, this.screenQuadVBO);
    const positionLocation = gl.getAttribLocation(this.program, 'vertexPositionNDC');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Bind patch textures
    for (let i = 0; i < shaderConfig.num_patches; i++) {
      gl.activeTexture(gl.TEXTURE0 + i);
      gl.bindTexture(gl.TEXTURE_2D, this.patchTextures[i]);
      gl.uniform1i(gl.getUniformLocation(this.program, `patch${i}`), i);
    }

    // Bind source image texture
    gl.activeTexture(gl.TEXTURE0 + shaderConfig.num_patches);
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    gl.uniform1i(
      gl.getUniformLocation(this.program, 'ref_img'),
      shaderConfig.num_patches,
    );

    // Set uniforms
    gl.uniform1f(gl.getUniformLocation(this.program, 'gamma'), shaderConfig.gamma);

    // tile_scale: nailAreaCm / patch_size (both in cm) — controls how many patch tiles span the artwork
    gl.uniform2f(
      gl.getUniformLocation(this.program, 'tile_scale'),
      nailAreaWidthCm / shaderConfig.patch_size,
      nailAreaHeightCm / shaderConfig.patch_size,
    );

    gl.uniform2f(
      gl.getUniformLocation(this.program, 'hist_clip'),
      shaderConfig.min_clip / 255.0,
      shaderConfig.max_clip / 255.0,
    );

    gl.uniform2f(
      gl.getUniformLocation(this.program, 'Tinvsize'),
      1.0 / processingWidth,
      1.0 / processingHeight,
    );

    // Draw
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    return this.canvas;
  }

  dispose() {
    if (!this.gl) return;

    const gl = this.gl;

    if (this.program) {
      gl.deleteProgram(this.program);
    }

    if (this.screenQuadVBO) {
      gl.deleteBuffer(this.screenQuadVBO);
    }

    if (this.sourceTexture) {
      gl.deleteTexture(this.sourceTexture);
    }

    this.patchTextures.forEach((texture) => {
      gl.deleteTexture(texture);
    });

    this.patchTextures = [];
    this.initialized = false;
  }
}

// ---------------------------------------------------------------------------
// Model3DBuilder
// ---------------------------------------------------------------------------

export class Model3DBuilder {
  constructor(config) {
    this.config = config;
    this.objLoader = new OBJLoader();
    this.frameCache = {};
  }

  /**
   * Build the complete 3D model.
   * @param {Object} state - Current configurator state
   * @param {HTMLCanvasElement} stringArtCanvas - Processed string art canvas
   * @returns {Promise<THREE.Group>}
   */
  async buildModel(state, stringArtCanvas) {
    const group = new THREE.Group();

    const sizeConfig = this.config.size_type?.[state.selected.size];
    const frameConfig = this.config.frame_type?.[state.selected.frame_type];
    if (!sizeConfig || !frameConfig) {
      console.error('[Model3D] Missing config — size:', state.selected.size, 'frame:', state.selected.frame_type);
      return new THREE.Group();
    }
    const frameColorId = state.selected.frame_color;

    // Get background type and base color from style configuration
    const styleKey = state.selected.style ?? 'black_weiss';
    const styleConfig = this.config.style?.[styleKey];
    const backgroundType = styleConfig?.background_type ?? 'weiss';
    const backgroundBaseColor = styleConfig?.background_base_color ?? '#ffffff';

    // Check if aspect ratio was swapped (rotated) and swap dimensions accordingly
    const isSwapped = state.isAspectRatioSwapped ?? false;

    // Convert mm to Three.js units (1 unit = 1 mm)
    const plateWidth = isSwapped ? sizeConfig.plate_y : sizeConfig.plate_x;
    const plateHeight = isSwapped ? sizeConfig.plate_x : sizeConfig.plate_y;
    const plateThickness = frameConfig.nail_plate_thickness;
    // Visual depth of the plate geometry — smaller than nail_plate_thickness so the frame
    // doesn't penetrate the plate. Frame positioning still uses nail_plate_thickness.
    const plateVisualDepth = this.config.viewer_3d?.plate_visual_depth ?? plateThickness;
    const nailAreaWidth = isSwapped ? sizeConfig.nail_area_y : sizeConfig.nail_area_x;
    const nailAreaHeight = isSwapped ? sizeConfig.nail_area_x : sizeConfig.nail_area_y;
    const stringArtElevation = this.config.viewer_3d.string_art_elevation;

    console.log(
      `[Model3D] Building model with dimensions: plate ${plateWidth}x${plateHeight}mm, ` +
        `nail area ${nailAreaWidth}x${nailAreaHeight}mm` +
        (isSwapped ? ' (rotated)' : ''),
    );

    // 1. Build the base plate with background texture
    const plate = this.buildPlate(
      plateWidth,
      plateHeight,
      plateVisualDepth,
      backgroundType,
      backgroundBaseColor,
    );
    group.add(plate);

    // 2. Build the string art plane
    const stringArt = this.buildStringArtPlane(
      nailAreaWidth,
      nailAreaHeight,
      stringArtElevation,
      stringArtCanvas,
      state,
      frameConfig,
      plateVisualDepth,
    );
    group.add(stringArt);

    // 3. Build the frame (if not 'none')
    if (state.selected.frame_type !== 'none') {
      const frame = await this.buildFrame(
        plateWidth,
        plateHeight,
        plateThickness,
        frameConfig,
        frameColorId,
        state.selected.frame_type,
      );
      group.add(frame);
    }

    // Center the group at origin
    const box = new THREE.Box3().setFromObject(group);
    const center = box.getCenter(new THREE.Vector3());
    group.position.sub(center);

    return group;
  }

  /**
   * Build the base plate with background texture.
   * Material order: [right(+X), left(-X), top(+Y), bottom(-Y), front(+Z), back(-Z)]
   */
  buildPlate(width, height, thickness, backgroundType, backgroundBaseColor) {
    const geometry = new THREE.BoxGeometry(width, height, thickness);

    const textureLoader = new THREE.TextureLoader();

    const matConfig = this.config.viewer_3d?.materials?.plate ?? {};
    const frontConfig = matConfig.front ?? {};
    const backConfig = matConfig.back ?? {};
    const sidesConfig = matConfig.sides ?? {};

    const baseColor = new THREE.Color(backgroundBaseColor ?? '#ffffff');

    const frontMaterial = new THREE.MeshStandardMaterial({
      color: baseColor,
      roughness: frontConfig.roughness ?? 0.3,
      metalness: frontConfig.metalness ?? 0.0,
    });

    const backMaterial = new THREE.MeshStandardMaterial({
      color: new THREE.Color(backConfig.color ?? '#ffffff'),
      roughness: backConfig.roughness ?? 0.3,
      metalness: backConfig.metalness ?? 0.0,
      emissive: new THREE.Color(backConfig.emissive ?? '#ffffff'),
      emissiveIntensity: backConfig.emissive_intensity ?? 0,
    });

    const sideMaterial = new THREE.MeshStandardMaterial({
      color: new THREE.Color(backConfig.color ?? '#ffffff'),
      roughness: sidesConfig.roughness ?? 0.3,
      metalness: sidesConfig.metalness ?? 0.0,
    });

    // Resolve Shopify CDN texture URL — prefer the specific background type,
    // fall back to weiss if not present in the map
    const assetUrls = this.config._assetUrls;
    const textureUrl =
      assetUrls?.backgrounds?.[backgroundType] ?? assetUrls?.backgrounds?.weiss;

    if (textureUrl) {
      const backgroundTexture = textureLoader.load(
        textureUrl,
        (texture) => {
          console.log(`[Model3D] Background texture loaded: ${backgroundType}`);
          frontMaterial.map = texture;
          frontMaterial.needsUpdate = true;
          console.log('[Model3D] Front material updated with texture');
        },
        undefined,
        (error) => {
          console.error(
            `[Model3D] Failed to load background texture: ${backgroundType}`,
            error,
          );
        },
      );
      backgroundTexture.wrapS = THREE.ClampToEdgeWrapping;
      backgroundTexture.wrapT = THREE.ClampToEdgeWrapping;
    }

    const materials = [
      sideMaterial,  // right (+X) - thin edge
      sideMaterial,  // left  (-X) - thin edge
      sideMaterial,  // top   (+Y) - thin edge
      sideMaterial,  // bottom(-Y) - thin edge
      frontMaterial, // front (+Z) - large face WITH BACKGROUND TEXTURE
      backMaterial,  // back  (-Z) - large face
    ];

    const plate = new THREE.Mesh(geometry, materials);
    plate.castShadow = true;
    plate.receiveShadow = true;

    console.log(`[Model3D] Plate created with background: ${backgroundType}`);

    return plate;
  }

  /**
   * Build the string art plane positioned in front of the plate.
   */
  buildStringArtPlane(width, height, elevation, canvas, state, frameConfig, visualDepth) {
    const geometry = new THREE.PlaneGeometry(width, height);

    const styleKey = state.selected.style ?? 'black_weiss';
    const styleConfig = this.config.style?.[styleKey];
    const yarnColorHex = styleConfig?.yarn_color ?? '#000000';

    console.log(
      `[Model3D] String art with style: ${styleKey}, yarn color: ${yarnColorHex}`,
    );

    // Create a new canvas to apply yarn color with transparency
    const coloredCanvas = document.createElement('canvas');
    coloredCanvas.width = canvas.width;
    coloredCanvas.height = canvas.height;
    const ctx = coloredCanvas.getContext('2d');

    ctx.drawImage(canvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    const yarnColor = new THREE.Color(yarnColorHex);
    const r = Math.floor(yarnColor.r * 255);
    const g = Math.floor(yarnColor.g * 255);
    const b = Math.floor(yarnColor.b * 255);

    // Dark areas (low brightness) = string; light areas = no string
    for (let i = 0; i < data.length; i += 4) {
      const brightness = data[i]; // shader output is greyscale, R = G = B
      const alpha = 1.0 - brightness / 255.0;
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = Math.floor(alpha * 255);
    }

    ctx.putImageData(imageData, 0, 0);

    const texture = new THREE.CanvasTexture(coloredCanvas);
    texture.needsUpdate = true;

    const stringArtConfig = this.config.viewer_3d?.materials?.string_art ?? {};
    const materialType = stringArtConfig.type ?? 'lambert';

    let material;
    if (materialType === 'standard') {
      material = new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: stringArtConfig.opacity ?? 1.0,
        alphaTest: stringArtConfig.alpha_test ?? 0.01,
        roughness: stringArtConfig.roughness ?? 0.8,
        metalness: stringArtConfig.metalness ?? 0.0,
      });
    } else {
      material = new THREE.MeshLambertMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: stringArtConfig.opacity ?? 1.0,
        alphaTest: stringArtConfig.alpha_test ?? 0.01,
      });
    }

    const plane = new THREE.Mesh(geometry, material);

    // Position in front of the plate — use visualDepth if provided, else fall back to nail_plate_thickness
    const plateThickness = visualDepth ?? frameConfig.nail_plate_thickness;
    const stringArtZ = plateThickness / 2 + elevation;
    plane.position.z = stringArtZ;

    plane.castShadow = false;
    plane.receiveShadow = false;

    console.log(
      `[Model3D] String art plane positioned at Z = ${plane.position.z}mm`,
    );

    return plane;
  }

  /**
   * Load OBJ file for a frame side from the Shopify CDN URL.
   * We only have one OBJ for all frame types (frameStandard URL).
   * @param {string} frameType - frame type key (used as cache key)
   * @returns {Promise<THREE.Group>}
   */
  async loadFrameOBJ(frameType) {
    if (this.frameCache[frameType]) {
      const clone = this.frameCache[frameType].clone();
      clone.traverse(child => {
        if (child.isMesh) child.geometry = child.geometry.clone();
      });
      return clone;
    }

    return new Promise((resolve, reject) => {
      const objUrl = this.config._assetUrls.frameStandard;
      console.log(`[Model3D] Loading frame OBJ: ${objUrl}`);

      this.objLoader.load(
        objUrl,
        (object) => {
          console.log(`[Model3D] Frame OBJ loaded successfully: ${frameType}`);
          // Scale by 10 to convert from cm to mm
          object.scale.set(10, 10, 10);
          this.frameCache[frameType] = object;
          const clone = object.clone();
          clone.traverse(child => {
            if (child.isMesh) child.geometry = child.geometry.clone();
          });
          resolve(clone);
        },
        undefined,
        (error) => {
          console.error(`[Model3D] Error loading frame OBJ: ${frameType}`, error);
          reject(error);
        },
      );
    });
  }

  /**
   * Load corner piece OBJ from the Shopify CDN URL.
   * @returns {Promise<THREE.Object3D>}
   */
  async loadCornerPieceOBJ() {
    if (this.frameCache['corner_piece']) {
      const clone = this.frameCache['corner_piece'].clone();
      clone.traverse(child => {
        if (child.isMesh) child.geometry = child.geometry.clone();
      });
      return clone;
    }

    return new Promise((resolve, reject) => {
      const objUrl = this.config._assetUrls.frameCorner;
      console.log(`[Model3D] Loading corner piece OBJ: ${objUrl}`);

      this.objLoader.load(
        objUrl,
        (object) => {
          console.log('[Model3D] Corner piece OBJ loaded successfully');
          object.scale.set(10, 10, 10);
          this.frameCache['corner_piece'] = object;
          const clone = object.clone();
          clone.traverse(child => {
            if (child.isMesh) child.geometry = child.geometry.clone();
          });
          resolve(clone);
        },
        undefined,
        (error) => {
          console.error('[Model3D] Error loading corner piece OBJ', error);
          reject(error);
        },
      );
    });
  }

  /**
   * Build the picture frame around the plate using OBJ files with miter clipping planes.
   */
  async buildFrame(
    plateWidth,
    plateHeight,
    plateThickness,
    frameConfig,
    frameColorId,
    frameTypeKey,
  ) {
    const frameGroup = new THREE.Group();

    // Resolve frame color and finish from top-level config.frame_color map
    let frameColorHex = '#1c1c1c';
    let frameFinish   = 'brushed';
    if (frameColorId) {
      const fc = this.config.frame_color?.[frameColorId];
      if (fc?.hex)    frameColorHex = fc.hex;
      if (fc?.finish) frameFinish   = fc.finish;
    }
    const frameColor = new THREE.Color(frameColorHex);

    // Finish presets — override any JSON material config
    const finishPresets = {
      matte:    { roughness: 0.95, metalness: 0.0  },
      brushed:  { roughness: 0.30, metalness: 0.50 },
      metallic: { roughness: 0.15, metalness: 0.85 },
    };
    const finishValues = finishPresets[frameFinish] ?? finishPresets.brushed;

    console.log(
      `[Model3D] buildFrame called — frameColorId: "${frameColorId}", hex: "${frameColorHex}", finish: "${frameFinish}"`,
      '| available frame_color keys:', Object.keys(this.config.frame_color || {}),
    );

    const frameMaterial = new THREE.MeshStandardMaterial({
      color: frameColor,
      roughness: finishValues.roughness,
      metalness: finishValues.metalness,
      vertexColors: false,
    });

    const frameType = frameTypeKey ?? (frameConfig.has_plexi ? 'plexi' : 'standard');

    try {
      const frameObj = await this.loadFrameOBJ(frameType);

      const bbox = new THREE.Box3().setFromObject(frameObj);
      const objExtrusionLength = bbox.max.z - bbox.min.z;
      const profileWidth = bbox.max.x - bbox.min.x;

      console.log(
        `[Model3D] OBJ extrusion length: ${objExtrusionLength}mm, profile width: ${profileWidth}mm`,
      );

      const miterAllowance = Math.max(profileWidth, 6);

      // Precompute 45° diagonal clipping planes at each corner
      const cornerTopLeft    = new THREE.Vector3(-plateWidth / 2,  plateHeight / 2, 0);
      const cornerTopRight   = new THREE.Vector3( plateWidth / 2,  plateHeight / 2, 0);
      const cornerBottomLeft = new THREE.Vector3(-plateWidth / 2, -plateHeight / 2, 0);
      const cornerBottomRight= new THREE.Vector3( plateWidth / 2, -plateHeight / 2, 0);

      const diagTopLeftA = new THREE.Plane(
        new THREE.Vector3(1, 1, 0).normalize(),
        -new THREE.Vector3(1, 1, 0).normalize().dot(cornerTopLeft),
      );
      const diagTopRightA = new THREE.Plane(
        new THREE.Vector3(-1, 1, 0).normalize(),
        -new THREE.Vector3(-1, 1, 0).normalize().dot(cornerTopRight),
      );
      const diagBottomLeftA = new THREE.Plane(
        new THREE.Vector3(1, -1, 0).normalize(),
        -new THREE.Vector3(1, -1, 0).normalize().dot(cornerBottomLeft),
      );
      const diagBottomRightA = new THREE.Plane(
        new THREE.Vector3(-1, -1, 0).normalize(),
        -new THREE.Vector3(-1, -1, 0).normalize().dot(cornerBottomRight),
      );

      const diagTopLeftB = new THREE.Plane(
        new THREE.Vector3(-1, -1, 0).normalize(),
        -new THREE.Vector3(-1, -1, 0).normalize().dot(cornerTopLeft),
      );
      const diagTopRightB = new THREE.Plane(
        new THREE.Vector3(1, -1, 0).normalize(),
        -new THREE.Vector3(1, -1, 0).normalize().dot(cornerTopRight),
      );
      const diagBottomLeftB = new THREE.Plane(
        new THREE.Vector3(-1, 1, 0).normalize(),
        -new THREE.Vector3(-1, 1, 0).normalize().dot(cornerBottomLeft),
      );
      const diagBottomRightB = new THREE.Plane(
        new THREE.Vector3(1, 1, 0).normalize(),
        -new THREE.Vector3(1, 1, 0).normalize().dot(cornerBottomRight),
      );

      const sides = [
        {
          name: 'top',
          length: plateWidth + 2 * miterAllowance,
          position: new THREE.Vector3(
            -plateWidth / 2 - miterAllowance,
             plateHeight / 2,
            -plateThickness / 2 - bbox.min.y,
          ),
          rotation: new THREE.Euler(-Math.PI / 2, Math.PI / 2, Math.PI),
          clipPlanes: [diagTopLeftA, diagTopRightA],
        },
        {
          name: 'bottom',
          length: plateWidth + 2 * miterAllowance,
          position: new THREE.Vector3(
             plateWidth / 2 + miterAllowance,
            -plateHeight / 2,
            -plateThickness / 2 - bbox.min.y,
          ),
          rotation: new THREE.Euler(-Math.PI, -Math.PI / 2, Math.PI / 2),
          clipPlanes: [diagBottomLeftA, diagBottomRightA],
        },
        {
          name: 'left',
          length: plateHeight + 2 * miterAllowance,
          position: new THREE.Vector3(
            -plateWidth / 2,
            -plateHeight / 2 - miterAllowance,
            -plateThickness / 2 - bbox.min.y,
          ),
          rotation: new THREE.Euler(-Math.PI / 2, 0, Math.PI),
          clipPlanes: [diagBottomLeftB, diagTopLeftB],
        },
        {
          name: 'right',
          length: plateHeight + 2 * miterAllowance,
          position: new THREE.Vector3(
             plateWidth / 2,
             plateHeight / 2 + miterAllowance,
            -plateThickness / 2 - bbox.min.y,
          ),
          rotation: new THREE.Euler(Math.PI / 2, 0, 0),
          clipPlanes: [diagBottomRightB, diagTopRightB],
        },
      ];

      for (const side of sides) {
        const sideObj = frameObj.clone();
        const scaleZ = side.length / objExtrusionLength;

        sideObj.traverse((child) => {
          if (child.isMesh) {
            const mat = frameMaterial.clone();
            mat.clippingPlanes = side.clipPlanes;
            mat.clipIntersection = false;
            mat.needsUpdate = true;
            console.log(
              `[Model3D] Clipping planes set for ${side.name}:`,
              side.clipPlanes.map((p) => ({
                normal: p.normal.toArray(),
                constant: p.constant.toFixed(3),
              })),
            );
            child.material = mat;
            child.castShadow = false;
            child.receiveShadow = true;

            const geometry = child.geometry.clone();
            geometry.scale(1, 1, scaleZ);
            child.geometry = geometry;
          }
        });

        sideObj.rotation.copy(side.rotation);
        sideObj.position.copy(side.position);
        frameGroup.add(sideObj);

        console.log(
          `[Model3D] Added ${side.name} frame side (mitered), raw length: ${side.length}mm ` +
            `(miter allowance: ${miterAllowance}mm from OBJ profile), scaleZ: ${scaleZ.toFixed(3)}`,
        );
      }
    } catch (error) {
      console.warn(
        `[Model3D] OBJ file not found for frame type "${frameType}", using box-based fallback`,
      );

      const frameInwards  = frameConfig.frame_inwards  ?? 15;
      const frameOutwards = frameConfig.frame_outwards ?? 15;
      const frameToFront  = frameConfig.frame_to_front ?? 5;
      const frameToBack   = frameConfig.frame_to_back  ?? 5;

      const barWidth = (frameInwards + frameOutwards) || 20;
      const barDepth = plateThickness + frameToFront + frameToBack;

      const fallbackMat = new THREE.MeshStandardMaterial({
        color: frameColor,
        roughness: finishValues.roughness,
        metalness: finishValues.metalness,
        vertexColors: false,
      });

      const createBar = (w, h, d) => {
        const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), fallbackMat);
        mesh.castShadow = false;
        mesh.receiveShadow = true;
        return mesh;
      };

      const topBar = createBar(plateWidth + 2 * frameOutwards, barWidth, barDepth);
      topBar.position.set(0, plateHeight / 2 + barWidth / 2 - frameInwards, (frameToFront - frameToBack) / 2);
      frameGroup.add(topBar);

      const bottomBar = createBar(plateWidth + 2 * frameOutwards, barWidth, barDepth);
      bottomBar.position.set(0, -(plateHeight / 2 + barWidth / 2 - frameInwards), (frameToFront - frameToBack) / 2);
      frameGroup.add(bottomBar);

      const leftBar = createBar(barWidth, plateHeight + 2 * frameOutwards, barDepth);
      leftBar.position.set(-(plateWidth / 2 + barWidth / 2 - frameInwards), 0, (frameToFront - frameToBack) / 2);
      frameGroup.add(leftBar);

      const rightBar = createBar(barWidth, plateHeight + 2 * frameOutwards, barDepth);
      rightBar.position.set(plateWidth / 2 + barWidth / 2 - frameInwards, 0, (frameToFront - frameToBack) / 2);
      frameGroup.add(rightBar);

      console.log(
        `[Model3D] Fallback frame created with bars: ${barWidth}mm wide × ${barDepth}mm deep`,
      );
    }

    // Add plexi glass if configured
    if (frameConfig.has_plexi) {
      const plexiDistance = frameConfig.plexi_distance ?? 30;
      const plexiGeometry = new THREE.PlaneGeometry(plateWidth, plateHeight);
      const plexiConfig = this.config.viewer_3d?.materials?.plexi ?? {};

      const plexiMaterial = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(plexiConfig.color ?? '#ffffff'),
        transparent: true,
        opacity: plexiConfig.opacity ?? 0.3,
        side: THREE.DoubleSide,
        depthWrite: false,
        roughness: plexiConfig.roughness ?? 0.1,
        metalness: plexiConfig.metalness ?? 0.1,
        transmission: plexiConfig.transmission ?? 0.9,
        thickness: plexiConfig.thickness ?? 3,
        clearcoat: plexiConfig.clearcoat ?? 1.0,
        clearcoatRoughness: plexiConfig.clearcoat_roughness ?? 0.1,
      });

      const plexi = new THREE.Mesh(plexiGeometry, plexiMaterial);
      plexi.position.z = plateThickness / 2 + plexiDistance;
      plexi.castShadow = false;
      plexi.receiveShadow = true;

      console.log(
        `[Model3D] Plexi glass positioned at Z = ${plexi.position.z}mm (${plexiDistance}mm from plate front)`,
      );

      frameGroup.add(plexi);
    }

    // Add corner pieces if configured
    if (frameConfig.corner_piece) {
      try {
        const cornerPieceObj = await this.loadCornerPieceOBJ();
        const cornerConfig = this.config.viewer_3d?.materials?.corner_pieces ?? {};

        const cornerMaterial = new THREE.MeshStandardMaterial({
          color: new THREE.Color(cornerConfig.color ?? '#bec2cb'),
          roughness: cornerConfig.roughness ?? 0.3,
          metalness: cornerConfig.metalness ?? 0.9,
        });

        const cornerZ = -9;
        const cornerInwards = 5;

        const corners = [
          {
            name: 'topLeft',
            position: new THREE.Vector3(-plateWidth / 2 + cornerInwards,  plateHeight / 2 - cornerInwards, cornerZ),
            rotation: new THREE.Euler(0, 0, Math.PI / 2),
          },
          {
            name: 'topRight',
            position: new THREE.Vector3( plateWidth / 2 - cornerInwards,  plateHeight / 2 - cornerInwards, cornerZ),
            rotation: new THREE.Euler(0, 0, 0),
          },
          {
            name: 'bottomLeft',
            position: new THREE.Vector3(-plateWidth / 2 + cornerInwards, -plateHeight / 2 + cornerInwards, cornerZ),
            rotation: new THREE.Euler(0, 0, Math.PI),
          },
          {
            name: 'bottomRight',
            position: new THREE.Vector3( plateWidth / 2 - cornerInwards, -plateHeight / 2 + cornerInwards, cornerZ),
            rotation: new THREE.Euler(0, 0, -Math.PI / 2),
          },
        ];

        for (const corner of corners) {
          const cornerObj = cornerPieceObj.clone();

          cornerObj.traverse((child) => {
            if (child.isMesh) {
              child.material = cornerMaterial.clone();
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });

          cornerObj.position.copy(corner.position);
          cornerObj.rotation.copy(corner.rotation);
          frameGroup.add(cornerObj);

          console.log(
            `[Model3D] Added corner piece at ${corner.name}: ` +
              `position=(${corner.position.x.toFixed(1)}, ${corner.position.y.toFixed(1)}, ${corner.position.z.toFixed(1)}), ` +
              `rotation=${((corner.rotation.z * 180) / Math.PI).toFixed(0)}°`,
          );
        }
      } catch (error) {
        console.error('[Model3D] Failed to load corner pieces:', error);
      }
    }

    return frameGroup;
  }

  // ── Circular model ──────────────────────────────────────────────────────────

  /**
   * Build the complete 3D model for a circular board.
   *
   * @param {Object}            state       - Configurator state
   * @param {HTMLCanvasElement} artCanvas   - Pre-rendered string art canvas (transparent BG)
   * @returns {THREE.Group}
   */
  buildCircularModel(state, artCanvas) {
    const group = new THREE.Group();

    const sizeConfig = this.config.size_type?.[state.selected.size];
    if (!sizeConfig) {
      console.error('[Model3D] Missing circle size config for key:', state.selected.size);
      return group;
    }

    const outerR      = (sizeConfig.circle_diameter    || 300) / 2;
    const ringThick   = sizeConfig.circle_ring_thickness || 0;
    const boardThick  = sizeConfig.nail_plate_thickness  || 18;
    const nailR       = (sizeConfig.nail_area_diameter   || (outerR * 2 - 20)) / 2;
    const elevation   = this.config.viewer_3d?.string_art_elevation ?? 1;

    const styleKey   = state.selected.style ?? 'black_weiss';
    const styleConf  = this.config.style?.[styleKey];
    const bgColor    = new THREE.Color(styleConf?.background_base_color ?? '#ffffff');
    const bgUrl      = this.config._assetUrls?.backgrounds?.[styleKey] ?? null;

    // ── Build board ──────────────────────────────────────────────────────────
    const frameConfig = this.config.frame_type?.[state.selected.frame_type];
    const boardStyle  = frameConfig?.board_style  ?? 'flat';
    const bevelRadius = frameConfig?.bevel_radius ?? 6;

    if (ringThick > 0) {
      group.add(this._buildRingBoard(outerR, ringThick, boardThick, bgColor, bgUrl));
    } else if (boardStyle === 'beveled') {
      group.add(this._buildBeveledDisk(outerR, boardThick, bevelRadius, bgColor, bgUrl));
    } else {
      group.add(this._buildSolidDisk(outerR, boardThick, bgColor, bgUrl));
    }

    // ── String art plane ─────────────────────────────────────────────────────
    const { mesh: artMesh, texture: artTexture } =
      this._buildCircleArtPlane(nailR, elevation, boardThick, artCanvas);
    artMesh.userData.artTexture = artTexture;
    group.add(artMesh);

    // ── Center group ─────────────────────────────────────────────────────────
    const box = new THREE.Box3().setFromObject(group);
    const center = box.getCenter(new THREE.Vector3());
    group.position.sub(center);

    return group;
  }

  /**
   * Update only the background colour of an existing circular model in-place.
   * Used when style changes but the art texture does not need to be regenerated
   * (e.g. frame/color change, or style change that doesn't affect inversion).
   * Leaves the art-plane mesh untouched.
   */
  updateCircularDiskColor(model, state) {
    const styleKey  = state.selected.style ?? 'black_weiss';
    const styleConf = this.config.style?.[styleKey];
    const bgColor   = new THREE.Color(styleConf?.background_base_color ?? '#ffffff');
    const bgUrl     = this.config._assetUrls?.backgrounds?.[styleKey] ?? null;
    model.traverse(obj => {
      if (obj.isMesh && !obj.userData.artTexture && obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach(m => {
          m.color.set(bgColor);
          if (!obj.userData.skipTexture) {
            if (bgUrl) {
              this._loadDiskTexture(bgUrl, m);
            } else {
              m.map = null;
              m.needsUpdate = true;
            }
          }
        });
      }
    });
  }

  /** Load a texture and apply it to a material. Returns the THREE.Texture. */
  _loadDiskTexture(bgUrl, mat) {
    if (!bgUrl) return;
    const tex = new THREE.TextureLoader().load(bgUrl, () => {
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      mat.map = tex;
      mat.needsUpdate = true;
    });
  }

  /** Solid cylinder board (ring_thickness = 0). */
  _buildSolidDisk(outerR, boardThick, bgColor, bgUrl) {
    const mat = new THREE.MeshStandardMaterial({ color: bgColor, roughness: 0.4, metalness: 0.0 });
    this._loadDiskTexture(bgUrl, mat);

    const geo  = new THREE.CylinderGeometry(outerR, outerR, boardThick, 64);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = Math.PI / 2;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  /**
   * Beveled disk — LatheGeometry profile with a quarter-circle rounding on the outer edge.
   * The profile is defined in (r, z) space and revolved around the Z axis (after rotation).
   *
   * Profile cross-section (right half):
   *
   *    ────────────── outerR ─────── bR ──
   *    flat top face                     ╮  ← quarter-circle arc, radius = bevelRadius
   *    ───────────────────────────────── │
   *                                      │  ← straight side
   *    ───────────────────────────────── ╯
   *    flat bottom face
   *
   * @param {number} outerR       Outer radius in mm.
   * @param {number} boardThick   Total board thickness in mm.
   * @param {number} bevelRadius  Corner rounding radius in mm (≤ boardThick/2).
   * @param {THREE.Color} bgColor Board fill colour.
   * @param {string} bgType       Key into asset URL map for a texture.
   */
  _buildBeveledDisk(outerR, boardThick, bevelRadius, bgColor, bgUrl) {
    const bR    = Math.min(bevelRadius, boardThick / 2, outerR * 0.25);
    const halfH = boardThick / 2;
    const ARC_STEPS = 12;  // segments per quarter-circle arc

    // Build 2D profile points in (r, z) — LatheGeometry revolves around Y.
    // We work in local Y=up space (Three.js CylinderGeometry convention) then rotate.
    // Profile goes counter-clockwise: bottom-centre → bottom edge → rounded corner → top edge → top-centre.
    const pts = [];

    // bottom face (inner → outer, z = -halfH)
    pts.push(new THREE.Vector2(0,       -halfH));
    pts.push(new THREE.Vector2(outerR - bR, -halfH));

    // bottom-outer rounded corner (quarter-circle arc sweeping from bottom to side)
    for (let i = 0; i <= ARC_STEPS; i++) {
      const angle = -Math.PI / 2 + (Math.PI / 2) * (i / ARC_STEPS); // -90° → 0°
      pts.push(new THREE.Vector2(
        outerR - bR + bR * Math.cos(angle),
        -halfH + bR + bR * Math.sin(angle),
      ));
    }

    // straight outer side (bottom corner top → top corner bottom)
    pts.push(new THREE.Vector2(outerR, halfH - bR));

    // top-outer rounded corner (quarter-circle arc sweeping from side to top)
    for (let i = 0; i <= ARC_STEPS; i++) {
      const angle = 0 + (Math.PI / 2) * (i / ARC_STEPS); // 0° → 90°
      pts.push(new THREE.Vector2(
        outerR - bR + bR * Math.cos(angle),
        halfH - bR + bR * Math.sin(angle),
      ));
    }

    // top face (outer → inner, z = +halfH)
    pts.push(new THREE.Vector2(0, halfH));

    // LatheGeometry UV maps the texture along the profile arc — wrong for the flat face.
    // Use it only for the edge body (skipTexture=true); add a CircleGeometry front face
    // with correct planar UV to correctly display the background texture.
    const edgeGeo  = new THREE.LatheGeometry(pts, 64);
    const edgeMat  = new THREE.MeshStandardMaterial({ color: bgColor, roughness: 0.4, metalness: 0.0 });
    const edgeMesh = new THREE.Mesh(edgeGeo, edgeMat);
    edgeMesh.rotation.x = Math.PI / 2;
    edgeMesh.castShadow    = true;
    edgeMesh.receiveShadow = true;
    edgeMesh.userData.skipTexture = true;  // LatheGeometry UV is wrong for flat face

    // Front face overlay: CircleGeometry has correct circular planar UV (center=0.5,0.5).
    // After the LatheGeometry's rotation.x=π/2, its local y=+halfH becomes world z=+halfH.
    // Use halfH + 0.5 so the art plane (placed at halfH + elevation ≥ 1) always renders above it.
    const frontGeo  = new THREE.CircleGeometry(outerR, 64);
    const frontMat  = new THREE.MeshStandardMaterial({ color: bgColor, roughness: 0.4, metalness: 0.0 });
    this._loadDiskTexture(bgUrl, frontMat);
    const frontMesh = new THREE.Mesh(frontGeo, frontMat);
    frontMesh.position.z  = halfH + 0.5;
    frontMesh.castShadow    = true;
    frontMesh.receiveShadow = true;
    frontMesh.userData.isFrontFace = true;

    const g = new THREE.Group();
    g.add(edgeMesh);
    g.add(frontMesh);
    return g;
  }

  /** Hollow ring board (ring_thickness > 0). */
  _buildRingBoard(outerR, ringThick, boardThick, bgColor, bgUrl) {
    const innerR = Math.max(1, outerR - ringThick);
    const g      = new THREE.Group();
    const mat    = new THREE.MeshStandardMaterial({ color: bgColor, roughness: 0.4, metalness: 0 });

    // Outer wall (open cylinder) — wood texture wraps around the border
    const outerGeo  = new THREE.CylinderGeometry(outerR, outerR, boardThick, 64, 1, true);
    const outerMat  = mat.clone();
    this._loadDiskTexture(bgUrl, outerMat);
    const outerMesh = new THREE.Mesh(outerGeo, outerMat);
    outerMesh.rotation.x = Math.PI / 2;
    g.add(outerMesh);

    // Inner wall (open cylinder, normals flipped toward inside — bgColor only)
    const innerGeo  = new THREE.CylinderGeometry(innerR, innerR, boardThick, 64, 1, true);
    const innerMat  = mat.clone(); innerMat.side = THREE.BackSide;
    const innerMesh = new THREE.Mesh(innerGeo, innerMat);
    innerMesh.rotation.x = Math.PI / 2;
    innerMesh.userData.skipTexture = true;
    g.add(innerMesh);

    // Front face: annular ring (correct hollow shape; RingGeometry faces +Z by default)
    const frontGeo  = new THREE.RingGeometry(innerR, outerR, 64);
    const frontMat  = mat.clone();
    this._loadDiskTexture(bgUrl, frontMat);
    const frontMesh = new THREE.Mesh(frontGeo, frontMat);
    frontMesh.position.z = boardThick / 2;
    frontMesh.userData.isFrontFace = true;
    g.add(frontMesh);

    // Back face: annular ring with wood texture (visible when board is tilted)
    const backGeo  = new THREE.RingGeometry(innerR, outerR, 64);
    const backMat  = mat.clone(); backMat.side = THREE.BackSide;
    this._loadDiskTexture(bgUrl, backMat);
    const backMesh = new THREE.Mesh(backGeo, backMat);
    backMesh.position.z = -boardThick / 2;
    g.add(backMesh);

    return g;
  }

  /**
   * Circular string art plane. Returns { mesh, texture }.
   *
   * Both `applyStringArtFilter` (colored) and `buildBWGenerator.render()` (B&W)
   * output a transparent-background canvas: dark threads = opaque, rest = transparent.
   * We back the CanvasTexture directly on `artCanvas` so live B&W updates work:
   * BWArtProcessor.render(artCanvas) → texture.needsUpdate = true → Three.js re-reads.
   */
  _buildCircleArtPlane(nailR, elevation, boardThick, artCanvas) {
    const texture = new THREE.CanvasTexture(artCanvas);
    texture.needsUpdate = true;

    const stringArtConfig = this.config.viewer_3d?.materials?.string_art ?? {};
    const material = new THREE.MeshLambertMaterial({
      map:         texture,
      side:        THREE.DoubleSide,
      transparent: true,
      alphaTest:   stringArtConfig.alpha_test ?? 0.01,
    });

    const geo  = new THREE.CircleGeometry(nailR, 128);
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.z = boardThick / 2 + elevation;
    mesh.castShadow = false;
    return { mesh, texture };
  }

  /**
   * Swap the art texture on an already-built circular model.
   * Used by BWArtProcessor for live iteration updates.
   *
   * @param {THREE.Group} group       - The model group
   * @param {HTMLCanvasElement} canvas - Updated art canvas (greyscale B&W output)
   */
  updateCircleArtTexture(group, canvas) {
    group.traverse(obj => {
      if (obj.userData.artTexture) {
        obj.userData.artTexture.needsUpdate = true;
      }
    });
  }

  /**
   * Dispose of all geometries, materials, and textures in a model group.
   * @param {THREE.Group} group
   */
  dispose(group) {
    if (!group) return;

    group.traverse((object) => {
      if (object.geometry) {
        object.geometry.dispose();
      }
      if (object.material) {
        const mats = Array.isArray(object.material) ? object.material : [object.material];
        mats.forEach(mat => {
          if (mat.map)              mat.map.dispose();
          if (mat.normalMap)        mat.normalMap.dispose();
          if (mat.roughnessMap)     mat.roughnessMap.dispose();
          if (mat.metalnessMap)     mat.metalnessMap.dispose();
          mat.dispose();
        });
      }
      // Dispose canvas textures stored in userData (circular art)
      if (object.userData.artTexture) {
        object.userData.artTexture.dispose();
      }
    });
  }
}

// ---------------------------------------------------------------------------
// Viewer3D
// ---------------------------------------------------------------------------

export class Viewer3D {
  constructor(containerElement, config) {
    this.container = containerElement;
    this.config = config;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.model = null;
    this.animationFrameId = null;
    this.stringArtShader    = null;
    this.coloredArtProcessor = null;
    this.bwArtProcessor      = null;
    this.modelBuilder = null;
    this.isInitialized = false;
    this.currentState = null;
    this.isUpdating = false;
    // Art cache: avoid re-running the shader/filter when only frame/color changes.
    // Key = "<size>|<isInverted>|<swapped>" — cleared when image URL changes.
    this._artCache = { key: null, canvas: null };
    this._cachedImageUrl = null;
    this._cachedBoardKey = null;
    this.zoomAnimation = null;
    this.rotationAnimation = null;
    this.currentAzimuthAngle = 0;
    this.currentPolarAngle = Math.PI / 2;
    this.currentZoomLevel = 1.0;
    this.optimalDistance = null;
    this.resizeTimeout = null;
    this.lastResizeWidth = 0;
    this._lastInnerWidth = 0;
    this._nativeWidth  = 0;
    this._nativeHeight = 0;

    // Lights
    this.mainLight = null;
    this.fillLight = null;
    this.rimLight = null;
    this.mainLightOffset = null;
    this.fillLightOffset = null;
    this.rimLightOffset = null;
  }

  async initialize() {
    if (this.isInitialized) return;

    // Lock the natural dimensions before any compact-mode CSS can affect them.
    // These are used for ALL renderer/camera operations so the canvas never
    // resizes just because the sticky header collapses on scroll.
    this._nativeWidth  = this.container.clientWidth;
    this._nativeHeight = this.container.clientHeight;

    console.log('[3D Viewer] Initializing...');
    console.log('[3D Viewer] Container:', this.container);
    console.log(
      '[3D Viewer] Container dimensions (native):',
      this._nativeWidth,
      'x',
      this._nativeHeight,
    );

    // Shader is only needed for nail boards (rectangular). Skip for circular.
    if (this.config.configurator_type !== 'circular') {
      console.log('[3D Viewer] Initializing shader (nail board path)...');
      this.stringArtShader = new StringArtShader(this.config);
      await this.stringArtShader.initialize();
      console.log('[3D Viewer] Shader initialized');
    } else {
      console.log('[3D Viewer] Skipping shader init (circular board)');
    }

    this.coloredArtProcessor = new ColoredArtProcessor();
    this.bwArtProcessor      = new BWArtProcessor();
    this.modelBuilder = new Model3DBuilder(this.config);

    this.setupScene();
    this.setupCamera();
    this.setupRenderer();
    //this.setupEnvironment();
    this.setupLights();
    this.setupControls();
    this.setupZoomButtons();

    console.log('[3D Viewer] Scene, camera, renderer, lights, controls setup complete');

    this._lastInnerWidth = window.innerWidth;
    window.addEventListener('resize', this.onWindowResize.bind(this));

    this.animate();

    this.isInitialized = true;
    console.log('[3D Viewer] Initialization complete');
  }

  setupScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf2f2f2);
  }

  setupCamera() {
    const aspect = this._nativeWidth / this._nativeHeight;
    this.camera = new THREE.PerspectiveCamera(45, aspect, 1, 8000);

    const viewerConfig = this.config.viewer_3d;
    this.camera.position.set(0, 0, viewerConfig.camera_distance_default);
    this.camera.lookAt(0, 0, 0);

    this.lastResizeWidth = this._nativeWidth;
  }

  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setSize(this._nativeWidth, this._nativeHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // Required for per-mesh 45° miter clipping planes
    this.renderer.localClippingEnabled = true;

    const canvas = this.renderer.domElement;
    canvas.style.display = 'block';
    // Absolute positioning so the compact sticky header can clip the canvas
    // via overflow:hidden without ever changing the renderer buffer size.
    this.container.style.position = 'relative';
    canvas.style.position = 'absolute';
    canvas.style.top    = '0';
    canvas.style.left   = '0';
    canvas.style.width  = this._nativeWidth  + 'px';
    canvas.style.height = this._nativeHeight + 'px';

    this.container.innerHTML = '';
    this.container.appendChild(canvas);

    console.log(
      '[3D Viewer] Renderer setup, canvas size:',
      canvas.width,
      'x',
      canvas.height,
    );
  }

  setupEnvironment() {
    // PMREMGenerator converts the RoomEnvironment scene into a pre-filtered radiance
    // environment map (PMREM). Setting scene.environment makes all PBR materials
    // (MeshStandardMaterial / MeshPhysicalMaterial) automatically pick it up for
    // specular reflections — critical for metallic surfaces to look different from matte.
    //const pmrem = new THREE.PMREMGenerator(this.renderer);
    //pmrem.compileEquirectangularShader();
    //const envTexture = pmrem.fromScene(new RoomEnvironment(), 0.01).texture;
    //this.scene.environment = envTexture;
    //pmrem.dispose();
    //console.log('[3D Viewer] Environment map set (RoomEnvironment)');
  }

  setupLights() {
    const lightConfig = this.config.viewer_3d?.lighting ?? {};

    // Ambient light
    const ambientConfig = lightConfig.ambient ?? {};
    const ambientLight = new THREE.AmbientLight(
      new THREE.Color(ambientConfig.color ?? '#ffffff'),
      ambientConfig.intensity ?? 2.0,
    );
    this.scene.add(ambientLight);

    // Main directional light (key light)
    const mainConfig = lightConfig.main ?? {};
    this.mainLightOffset = mainConfig.position ?? { x: 500, y: 800, z: 600 };
    this.mainLight = new THREE.DirectionalLight(
      new THREE.Color(mainConfig.color ?? '#ffffff'),
      mainConfig.intensity ?? 0.8,
    );
    this.mainLight.position.set(
      this.mainLightOffset.x,
      this.mainLightOffset.y,
      this.mainLightOffset.z,
    );
    if (mainConfig.cast_shadow !== false) {
      this.mainLight.castShadow = true;
      const shadowConfig = mainConfig.shadow ?? {};
      const camSize = shadowConfig.camera_size ?? 1000;
      this.mainLight.shadow.camera.left   = -camSize;
      this.mainLight.shadow.camera.right  =  camSize;
      this.mainLight.shadow.camera.top    =  camSize;
      this.mainLight.shadow.camera.bottom = -camSize;
      this.mainLight.shadow.camera.near   = shadowConfig.camera_near ?? 0.5;
      this.mainLight.shadow.camera.far    = shadowConfig.camera_far  ?? 3000;
      const mapSize = shadowConfig.map_size ?? 2048;
      this.mainLight.shadow.mapSize.width  = mapSize;
      this.mainLight.shadow.mapSize.height = mapSize;
      this.mainLight.shadow.bias   = shadowConfig.bias   ?? -0.0001;
      this.mainLight.shadow.radius = shadowConfig.radius ?? 2;
    }
    this.scene.add(this.mainLight);

    // Fill light (softer, from opposite side)
    const fillConfig = lightConfig.fill ?? {};
    this.fillLightOffset = fillConfig.position ?? { x: -500, y: 300, z: -400 };
    this.fillLight = new THREE.DirectionalLight(
      new THREE.Color(fillConfig.color ?? '#ffffff'),
      fillConfig.intensity ?? 0.3,
    );
    this.fillLight.position.set(
      this.fillLightOffset.x,
      this.fillLightOffset.y,
      this.fillLightOffset.z,
    );
    this.scene.add(this.fillLight);

    // Rim light (from behind, for depth)
    const rimConfig = lightConfig.rim ?? {};
    this.rimLightOffset = rimConfig.position ?? { x: 0, y: -200, z: -600 };
    this.rimLight = new THREE.DirectionalLight(
      new THREE.Color(rimConfig.color ?? '#ffffff'),
      rimConfig.intensity ?? 0.2,
    );
    this.rimLight.position.set(
      this.rimLightOffset.x,
      this.rimLightOffset.y,
      this.rimLightOffset.z,
    );
    this.scene.add(this.rimLight);

    // Optional spot light
    const spotConfig = lightConfig.spot ?? {};
    if ((spotConfig.intensity ?? 0) > 0) {
      const spotPos    = spotConfig.position ?? { x: 400, y: 600, z: 500 };
      const spotTarget = spotConfig.target   ?? { x: 0,   y: 0,   z: 0   };
      const spotLight  = new THREE.SpotLight(
        new THREE.Color(spotConfig.color ?? '#ffffff'),
        spotConfig.intensity,
        spotConfig.distance ?? 2000,
        ((spotConfig.angle_degrees ?? 30) * Math.PI) / 180,
        spotConfig.penumbra ?? 0.5,
        spotConfig.decay ?? 1,
      );
      spotLight.position.set(spotPos.x, spotPos.y, spotPos.z);
      spotLight.target.position.set(spotTarget.x, spotTarget.y, spotTarget.z);
      this.scene.add(spotLight);
      this.scene.add(spotLight.target);
      if (spotConfig.cast_shadow) {
        spotLight.castShadow = true;
        spotLight.shadow.mapSize.width  = 2048;
        spotLight.shadow.mapSize.height = 2048;
      }
    }

    // Optional rect area light
    const rectConfig = lightConfig.rect_area ?? {};
    if ((rectConfig.intensity ?? 0) > 0) {
      RectAreaLightUniformsLib.init();
      const rectPos    = rectConfig.position ?? { x: 0, y: 500, z: 800 };
      const rectLookAt = rectConfig.look_at  ?? { x: 0, y: 0,   z: 0   };
      const rectLight  = new THREE.RectAreaLight(
        new THREE.Color(rectConfig.color ?? '#ffffff'),
        rectConfig.intensity,
        rectConfig.width  ?? 500,
        rectConfig.height ?? 500,
      );
      rectLight.position.set(rectPos.x, rectPos.y, rectPos.z);
      rectLight.lookAt(rectLookAt.x, rectLookAt.y, rectLookAt.z);
      this.scene.add(rectLight);
    }
  }

  setupControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);

    const viewerConfig = this.config.viewer_3d;
    const touchConfig  = viewerConfig.touch_controls ?? {};

    this.controls.enableDamping = true;
    this.controls.dampingFactor = touchConfig.damping_factor ?? 0.05;
    this.controls.minDistance   = viewerConfig.camera_distance_min;
    this.controls.maxDistance   = viewerConfig.camera_distance_max;
    this.controls.enablePan     = false;
    this.controls.maxPolarAngle = Math.PI / 1.5;
    this.controls.minPolarAngle = Math.PI / 4;
    this.controls.target.set(0, 0, 0);

    this.controls.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_PAN,
    };

    this.controls.zoomSpeed   = touchConfig.zoom_speed   ?? 1.0;
    this.controls.rotateSpeed = touchConfig.rotate_speed ?? 0.5;
  }

  setupZoomButtons() {
    const zoomConfig = this.config.viewer_3d.zoom_buttons;
    if (!zoomConfig) return;

    const zoomControls = document.getElementById('zoomControls');
    const zoomInBtn    = document.getElementById('zoomInBtn');
    const zoomOutBtn   = document.getElementById('zoomOutBtn');

    if (!zoomControls || !zoomInBtn || !zoomOutBtn) return;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
    const shouldShow =
      (isMobile && zoomConfig.enabled_mobile) ||
      (!isMobile && zoomConfig.enabled_desktop);

    if (shouldShow) {
      zoomControls.style.display = 'flex';
    } else {
      zoomControls.style.display = 'none';
      return;
    }

    zoomInBtn.addEventListener('click', () => {
      if (!this.camera || !this.controls) return;
      const currentDistance = this.camera.position.distanceTo(this.controls.target);
      const targetDistance  = Math.max(
        currentDistance - zoomConfig.zoom_step,
        this.config.viewer_3d.camera_distance_min,
      );
      this.animateZoom(targetDistance);
    });

    zoomOutBtn.addEventListener('click', () => {
      if (!this.camera || !this.controls) return;
      const currentDistance = this.camera.position.distanceTo(this.controls.target);
      const targetDistance  = Math.min(
        currentDistance + zoomConfig.zoom_step,
        this.config.viewer_3d.camera_distance_max,
      );
      this.animateZoom(targetDistance);
    });
  }

  /**
   * Animate camera zoom to a target distance.
   * @param {number} endDistance
   */
  animateZoom(endDistance) {
    if (this.zoomAnimation) {
      this.zoomAnimation.cancelled = true;
    }

    const startDistance = this.camera.position.distanceTo(this.controls.target);
    const duration = this.config.viewer_3d.zoom_buttons?.animation_duration_ms ?? 300;
    const startTime = performance.now();
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animation = { cancelled: false };
    this.zoomAnimation = animation;

    const animate = (currentTime) => {
      if (animation.cancelled) return;

      const elapsed  = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased    = easeOutCubic(progress);
      const distance = startDistance + (endDistance - startDistance) * eased;

      const direction = new THREE.Vector3()
        .subVectors(this.camera.position, this.controls.target)
        .normalize();

      this.camera.position
        .copy(this.controls.target)
        .add(direction.multiplyScalar(distance));
      this.controls.update();

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.zoomAnimation = null;
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Animate the camera orbit to target spherical angles (azimuth + polar).
   * @param {number} targetAzimuthDegrees
   * @param {number} targetPolarDegrees
   * @param {number|null} zoomLevel
   */
  animateCameraOrbit(targetAzimuthDegrees, targetPolarDegrees, zoomLevel = null) {
    if (!this.camera || !this.controls) {
      console.warn('[3D Viewer] Cannot animate rotation - camera/controls not ready');
      return;
    }

    if (this.rotationAnimation) {
      this.rotationAnimation.cancelled = true;
    }

    const viewerConfig = this.config.viewer_3d ?? {};
    const duration = viewerConfig.rotation_animation_duration_ms ?? 500;
    const targetAzimuthRadians = (targetAzimuthDegrees * Math.PI) / 180;
    const targetPolarRadians   = (targetPolarDegrees   * Math.PI) / 180;

    const target = this.controls.target;
    const dx = this.camera.position.x - target.x;
    const dy = this.camera.position.y - target.y;
    const dz = this.camera.position.z - target.z;
    const startDistance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    const baseDistance    = this.optimalDistance ?? startDistance;
    const effectiveZoom   = zoomLevel ?? this.currentZoomLevel;
    const targetDistance  = baseDistance / effectiveZoom;

    // Track the active zoom level so resetCamera can preserve it
    if (zoomLevel !== null) this.currentZoomLevel = zoomLevel;

    const startAzimuthRadians = Math.atan2(dx, dz);
    const startPolarRadians   = Math.acos(Math.max(-1, Math.min(1, dy / startDistance)));

    const azimuthDiff  = Math.abs(startAzimuthRadians - targetAzimuthRadians);
    const polarDiff    = Math.abs(startPolarRadians   - targetPolarRadians);
    const distanceDiff = Math.abs(startDistance       - targetDistance);

    if (azimuthDiff < 0.001 && polarDiff < 0.001 && distanceDiff < 0.001) {
      return;
    }

    const startTime    = performance.now();
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animation  = { cancelled: false };
    this.rotationAnimation = animation;

    const targetVec = this.controls.target.clone();

    console.log(
      `[3D Viewer] Animating camera orbit: azimuth ${((startAzimuthRadians * 180) / Math.PI).toFixed(1)}° → ${targetAzimuthDegrees}°, ` +
        `polar ${((startPolarRadians * 180) / Math.PI).toFixed(1)}° → ${targetPolarDegrees}°, ` +
        `zoom ${zoomLevel ?? 'unchanged'}`,
    );

    const animate = (currentTime) => {
      if (animation.cancelled) return;

      const elapsed  = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased    = easeOutCubic(progress);

      const currentAzimuth  = startAzimuthRadians + (targetAzimuthRadians - startAzimuthRadians) * eased;
      const currentPolar    = startPolarRadians   + (targetPolarRadians   - startPolarRadians)   * eased;
      const currentDistance = startDistance       + (targetDistance       - startDistance)       * eased;

      const newX = targetVec.x + currentDistance * Math.sin(currentPolar) * Math.sin(currentAzimuth);
      const newY = targetVec.y + currentDistance * Math.cos(currentPolar);
      const newZ = targetVec.z + currentDistance * Math.sin(currentPolar) * Math.cos(currentAzimuth);

      this.camera.position.set(newX, newY, newZ);
      this.camera.lookAt(targetVec);
      this.controls.update();

      this.currentAzimuthAngle = currentAzimuth;
      this.currentPolarAngle   = currentPolar;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.rotationAnimation = null;
        console.log('[3D Viewer] Camera orbit animation complete');
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Rotate to the configured angle for a specific wizard step.
   * @param {string} stepKey
   * @param {string|null} previousStepKey
   */
  rotateToStepAngle(stepKey, previousStepKey = null) {
    const viewerConfig    = this.config.viewer_3d ?? {};
    const stepRotations   = viewerConfig.step_rotations ?? {};
    const stepConfig      = stepRotations[stepKey];
    const previousConfig  = previousStepKey ? stepRotations[previousStepKey] : null;

    if (stepConfig && stepConfig.enabled) {
      const targetAzimuth = stepConfig.azimuth_degrees ?? 0;
      const targetPolar   = stepConfig.polar_degrees   ?? 90;
      const zoomLevel     = stepConfig.zoom_level      ?? null;
      this.animateCameraOrbit(targetAzimuth, targetPolar, zoomLevel);
    } else if (previousConfig && previousConfig.enabled) {
      this.rotateToDefaultAngle();
    }
  }

  /**
   * Rotate back to the default viewing angle.
   */
  rotateToDefaultAngle() {
    const viewerConfig    = this.config.viewer_3d ?? {};
    const defaultRotation = viewerConfig.default_rotation ?? {};
    const defaultAzimuth  = defaultRotation.azimuth_degrees ?? 0;
    const defaultPolar    = defaultRotation.polar_degrees   ?? 90;
    const defaultZoom     = viewerConfig.default_zoom       ?? 1.0;
    this.animateCameraOrbit(defaultAzimuth, defaultPolar, defaultZoom);
  }

  onWindowResize() {
    if (!this.camera || !this.renderer) return;

    // Mobile browsers fire resize when the address bar shows/hides during scroll,
    // changing only window.innerHeight. Ignore those height-only events.
    const newWidth = window.innerWidth;
    if (Math.abs(newWidth - this._lastInnerWidth) < 5) return;

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this._lastInnerWidth = window.innerWidth;
      this.handleResize();
    }, 150);
  }

  handleResize() {
    if (!this.camera || !this.renderer) return;

    // Only handle genuine width changes (e.g. orientation change).
    // Height-only changes (mobile chrome show/hide) are already filtered
    // in onWindowResize; we also guard here in case of direct calls.
    const currentWidth = this.container.clientWidth;
    if (Math.abs(currentWidth - this.lastResizeWidth) <= 10) return;

    // Update locked native width; keep native height (no compact-mode pollution).
    this._nativeWidth = currentWidth;

    this.camera.aspect = this._nativeWidth / this._nativeHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this._nativeWidth, this._nativeHeight);

    // Keep canvas CSS in sync with the buffer size.
    const canvas = this.renderer.domElement;
    canvas.style.width  = this._nativeWidth  + 'px';
    canvas.style.height = this._nativeHeight + 'px';

    this.lastResizeWidth = this._nativeWidth;
    if (this.model) this.resetCamera(false);
  }

  /**
   * Adjust viewer height responsively on small screens.
   */
  adjustResponsiveHeight() {
    console.log('[3D Viewer] adjustResponsiveHeight called');
    const responsiveConfig = this.config.viewer_3d?.responsive_height;
    console.log('[3D Viewer] responsiveConfig:', responsiveConfig);

    if (!responsiveConfig || !responsiveConfig.enabled) {
      console.log('[3D Viewer] Responsive height disabled or config missing');
      return;
    }

    const screenWidth    = window.innerWidth;
    const threshold      = responsiveConfig.screen_width_threshold ?? 600;
    const containerWidth = this.container.clientWidth;
    console.log(
      '[3D Viewer] screenWidth:', screenWidth,
      'threshold:', threshold,
      'containerWidth:', containerWidth,
    );

    const viewPlaceholder = this.container.parentElement;
    console.log('[3D Viewer] viewPlaceholder:', viewPlaceholder);
    if (!viewPlaceholder) {
      console.log('[3D Viewer] No parent element found');
      return;
    }

    if (screenWidth < threshold) {
      console.log('[3D Viewer] Small screen detected, adjusting height');
      const defaultHeight = responsiveConfig.default_height ?? 500;
      const minHeight     = responsiveConfig.min_height     ?? 300;
      console.log('[3D Viewer] defaultHeight:', defaultHeight, 'minHeight:', minHeight);

      if (this.model && this.camera) {
        console.log('[3D Viewer] Model exists, calculating optimal height');
        const box  = new THREE.Box3().setFromObject(this.model);
        const size = box.getSize(new THREE.Vector3());
        console.log(
          '[3D Viewer] Model size - x:', size.x.toFixed(2),
          'y:', size.y.toFixed(2),
          'z:', size.z.toFixed(2),
        );

        const fov          = this.camera.fov * (Math.PI / 180);
        const viewerConfig = this.config.viewer_3d;
        const paddingConfig = viewerConfig.camera_padding ?? {
          horizontal: 1.2,
          vertical_landscape: 1.2,
          vertical_portrait: 1.6,
        };

        const verticalPadding = paddingConfig.vertical_portrait ?? 1.6;
        console.log(
          '[3D Viewer] Using vertical padding:', verticalPadding,
          'horizontal padding:', paddingConfig.horizontal,
        );

        let optimalHeight = defaultHeight;

        for (let testHeight = defaultHeight; testHeight >= minHeight; testHeight -= 20) {
          const aspect = containerWidth / testHeight;

          const distanceForWidth  = Math.abs(
            (size.x * paddingConfig.horizontal) / (2 * Math.tan(fov / 2) * aspect),
          );
          const distanceForHeight = Math.abs(
            (size.y * verticalPadding) / (2 * Math.tan(fov / 2)),
          );

          console.log(
            `[3D Viewer] Testing height ${testHeight}px: distanceForWidth=${distanceForWidth.toFixed(2)}, distanceForHeight=${distanceForHeight.toFixed(2)}`,
          );

          if (distanceForHeight > distanceForWidth) {
            console.log(`[3D Viewer] Height is now limiting constraint at ${testHeight}px`);
            break;
          }

          optimalHeight = testHeight;
        }

        console.log('[3D Viewer] Setting height to:', optimalHeight, 'px');
        viewPlaceholder.style.height = `${optimalHeight}px`;
      } else {
        console.log('[3D Viewer] No model yet, using default height:', defaultHeight);
        viewPlaceholder.style.height = `${defaultHeight}px`;
      }
    } else {
      console.log('[3D Viewer] Large screen detected, using default height');
      const defaultHeight = responsiveConfig.default_height ?? 500;
      viewPlaceholder.style.height = `${defaultHeight}px`;
    }
  }

  animate() {
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));

    if (this.controls) {
      this.controls.update();
    }

    // Keep lights following the camera
    if (this.camera) {
      const camPos = this.camera.position;
      if (this.mainLight && this.mainLightOffset) {
        this.mainLight.position.set(
          camPos.x + this.mainLightOffset.x,
          camPos.y + this.mainLightOffset.y,
          camPos.z + this.mainLightOffset.z,
        );
      }
      if (this.fillLight && this.fillLightOffset) {
        this.fillLight.position.set(
          camPos.x + this.fillLightOffset.x,
          camPos.y + this.fillLightOffset.y,
          camPos.z + this.fillLightOffset.z,
        );
      }
      if (this.rimLight && this.rimLightOffset) {
        this.rimLight.position.set(
          camPos.x + this.rimLightOffset.x,
          camPos.y + this.rimLightOffset.y,
          camPos.z + this.rimLightOffset.z,
        );
      }
    }

    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  /**
   * Update the 3D model based on the current configurator state.
   * @param {Object} state
   */
  async updateModel(state) {
    console.log('[3D Viewer] updateModel called', state);

    if (this.isUpdating) {
      console.warn('[3D Viewer] Update already in progress, skipping duplicate call');
      return;
    }

    this.isUpdating = true;

    try {
      if (!this.isInitialized) {
        console.log('[3D Viewer] Not initialized, initializing now...');
        await this.initialize();
      }

      if (!state.uploadedDataUrl) {
        console.warn('[3D Viewer] No uploaded image to display');
        this.showPlaceholder();
        return;
      }

      // Track whether a model was already showing before this update
      const hadModel = !!this.model;

      console.log('[3D Viewer] State selections:', {
        size: state.selected.size,
        frame_type: state.selected.frame_type,
        frame_color: state.selected.frame_color,
        style: state.selected.style,
      });

      // Remove placeholder helpers if present
      const placeholder = this.scene.getObjectByName('placeholder');
      if (placeholder) this.scene.remove(placeholder);
      const axes = this.scene.getObjectByName('placeholder-axes');
      if (axes) this.scene.remove(axes);

      this.currentState = state;

      // ── Art-cache invalidation ──────────────────────────────────────────────
      // Clear cache whenever the source image changes (new upload / crop).
      if (state.uploadedDataUrl !== this._cachedImageUrl) {
        this._artCache = { key: null, canvas: null };
        this._cachedImageUrl = state.uploadedDataUrl;
      }

      // Optionally invert for white yarn styles
      const styleKey    = state.selected.style ?? 'black_weiss';
      const styleConfig = this.config.style?.[styleKey];
      const isInverted  = styleConfig?.is_inverted ?? false;

      const sizeConfig = this.config.size_type?.[state.selected.size];
      if (!sizeConfig) {
        console.error('[3D Viewer] Unknown size key:', state.selected.size, '— available:', Object.keys(this.config.size_type || {}));
        return;
      }

      // Art key: changes only when size, inversion or aspect-ratio-swap change.
      // Frame type / frame color / board style changes do NOT affect the art texture.
      const artKey     = `${state.selected.size}|${isInverted}|${state.isAspectRatioSwapped ?? false}`;
      const needsArt   = this._artCache.key !== artKey || !this._artCache.canvas;

      // ── Route by board shape ──────────────────────────────────────────────
      let newModel;

      if (this.config.configurator_type === 'circular') {
        const artType      = this.config.circular_art_type || 'colored';
        const circleSizePx = Math.round((sizeConfig.circle_diameter || 300) * (sizeConfig.art_size_multiplier || 3));

        if (artType === 'bw') {
          const bwFrameConfig = this.config.frame_type?.[state.selected.frame_type];
          const boardKey = `${state.selected.frame_type}|${bwFrameConfig?.board_style ?? 'flat'}|${bwFrameConfig?.bevel_radius ?? 6}`;
          const boardChanged = boardKey !== this._cachedBoardKey;

          if (!needsArt && this.model) {
            if (!boardChanged) {
              // ── BW, art key unchanged, board unchanged: only update disk colour ──
              console.log('[3D Viewer] BW cache hit — updating disk color only');
              this.modelBuilder.updateCircularDiskColor(this.model, state);
              return;   // skip model swap (finally block still runs)
            }
            // ── BW, art key unchanged but board shape changed: rebuild model, keep generator ──
            console.log('[3D Viewer] BW board shape changed — rebuilding model');
            this._cachedBoardKey = boardKey;
            newModel = this.modelBuilder.buildCircularModel(state, this._artCache.canvas);
            const shapeMesh = (() => { let f = null; newModel.traverse(o => { if (o.userData.artTexture) f = o; }); return f; })();
            if (shapeMesh && this.bwArtProcessor) {
              this.bwArtProcessor.setTexture(shapeMesh.userData.artTexture);
            }
            // fall through to model swap — no generator restart needed
          } else {
            // ── BW, art key changed: full rebuild ──
            this._cachedBoardKey = boardKey;
            if (this.bwArtProcessor) this.bwArtProcessor.stop();

            console.log('[3D Viewer] Loading image to canvas...');
            const sourceCanvas  = await this.loadImageToCanvas(state.uploadedDataUrl);
            const processCanvas = isInverted ? this.invertImage(sourceCanvas) : sourceCanvas;

            const artCanvas = document.createElement('canvas');
            artCanvas.width = artCanvas.height = circleSizePx;
            const ctx = artCanvas.getContext('2d');
            ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, circleSizePx, circleSizePx);

            this._artCache = { key: artKey, canvas: artCanvas };

            newModel = this.modelBuilder.buildCircularModel(state, artCanvas);

            const artMesh = (() => {
              let found = null;
              newModel.traverse(o => { if (o.userData.artTexture) found = o; });
              return found;
            })();

            if (artMesh) {
              const texture = artMesh.userData.artTexture;
              const bwParams = {
                ...(this.config.bw_art || {}),
                ...(sizeConfig.bw_nail_count    != null && { nailCount:   sizeConfig.bw_nail_count }),
                ...(sizeConfig.bw_max_lines     != null && { maxLines:    sizeConfig.bw_max_lines }),
                ...(sizeConfig.bw_line_alpha    != null && { lineAlpha:   sizeConfig.bw_line_alpha }),
                ...(sizeConfig.bw_min_angle_deg != null && { minAngleDeg: sizeConfig.bw_min_angle_deg }),
              };
              this.bwArtProcessor.start(processCanvas, bwParams, artCanvas, texture).catch(err => {
                console.error('[3D Viewer] B&W art processor error:', err);
              });
            }
          }
        } else {
          // ── Colored circular ──
          let artCanvas;
          if (needsArt) {
            console.log('[3D Viewer] Loading image to canvas...');
            const sourceCanvas  = await this.loadImageToCanvas(state.uploadedDataUrl);
            const processCanvas = isInverted ? this.invertImage(sourceCanvas) : sourceCanvas;

            const coloredParams = {
              ...(this.config.colored_art || {}),
              size: circleSizePx,
              ...(sizeConfig.art_line_width != null && { lineWidth: sizeConfig.art_line_width }),
            };
            artCanvas = this.coloredArtProcessor.process(processCanvas, coloredParams);
            this._artCache = { key: artKey, canvas: artCanvas };
            console.log('[3D Viewer] Colored art rendered, canvas:', circleSizePx, 'px');
          } else {
            artCanvas = this._artCache.canvas;
            console.log('[3D Viewer] Colored art cache hit — reusing canvas');
          }
          newModel = this.modelBuilder.buildCircularModel(state, artCanvas);
        }
      } else {
        // ── Nail board ────────────────────────────────────────────────────
        const isSwapped = state.isAspectRatioSwapped ?? false;

        if (!this.stringArtShader) {
          console.error('[3D Viewer] Shader not initialized — configurator_type must be "nail" to use the shader');
          return;
        }

        if (!sizeConfig.nail_area_x || !sizeConfig.nail_area_y) {
          console.error('[3D Viewer] nail_area_x/y missing for size', state.selected.size, sizeConfig);
          return;
        }

        let stringArtCanvas;
        if (needsArt) {
          console.log('[3D Viewer] Loading image to canvas...');
          const sourceCanvas  = await this.loadImageToCanvas(state.uploadedDataUrl);
          const processCanvas = isInverted ? this.invertImage(sourceCanvas) : sourceCanvas;

          const nailAreaWidth  = isSwapped ? sizeConfig.nail_area_y : sizeConfig.nail_area_x;
          const nailAreaHeight = isSwapped ? sizeConfig.nail_area_x : sizeConfig.nail_area_y;

          console.log(
            '[3D Viewer] Processing with shader, nail area:',
            nailAreaWidth / 10, 'x', nailAreaHeight / 10, 'cm',
            isSwapped ? '(rotated)' : '',
          );

          stringArtCanvas = this.stringArtShader.processImage(
            processCanvas,
            nailAreaWidth  / 10,
            nailAreaHeight / 10,
          );
          this._artCache = { key: artKey, canvas: stringArtCanvas };
          console.log('[3D Viewer] Shader processing complete');
        } else {
          stringArtCanvas = this._artCache.canvas;
          console.log('[3D Viewer] Shader art cache hit — reusing canvas');
        }

        newModel = await this.modelBuilder.buildModel(state, stringArtCanvas);
      }

      console.log('[3D Viewer] Building 3D model complete');

      // Atomic swap: remove old, add new in the same frame
      if (this.model) {
        console.log('[3D Viewer] Removing existing model');
        this.scene.remove(this.model);
        this.modelBuilder.dispose(this.model);
      }
      this.model = newModel;
      this.scene.add(this.model);
      console.log('[3D Viewer] Model swapped');

      if (this.renderer) {
        // Always use the locked native dimensions — never let compact-mode
        // container height (160px) corrupt the renderer buffer.
        this.camera.aspect = this._nativeWidth / this._nativeHeight;
        this.camera.updateProjectionMatrix();
      }

      // First load: snap camera instantly. Subsequent updates: animate smoothly.
      this.resetCamera(false, hadModel);
      console.log('[3D Viewer] Camera reset, model update complete');
    } catch (error) {
      console.error('[3D Viewer] Error updating 3D model:', error);
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Show an empty scene (no model, no placeholder helpers).
   */
  showPlaceholder() {
    console.log('[3D Viewer] Showing placeholder - no grid, waiting for default example');

    if (this.model) {
      this.scene.remove(this.model);
      this.model = null;
    }

    const existing = this.scene.getObjectByName('placeholder');
    if (existing) this.scene.remove(existing);

    const axes = this.scene.getObjectByName('placeholder-axes');
    if (axes) this.scene.remove(axes);

    this.resetCamera();
  }

  /**
   * Load a data URL into an HTMLCanvasElement.
   * @param {string} dataUrl
   * @returns {Promise<HTMLCanvasElement>}
   */
  loadImageToCanvas(dataUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width  = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  }

  /**
   * Invert pixel values of a canvas (for white yarn styles).
   * @param {HTMLCanvasElement} sourceCanvas
   * @returns {HTMLCanvasElement}
   */
  invertImage(sourceCanvas) {
    const canvas = document.createElement('canvas');
    canvas.width  = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(sourceCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data      = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      data[i]     = 255 - data[i];     // R
      data[i + 1] = 255 - data[i + 1]; // G
      data[i + 2] = 255 - data[i + 2]; // B
      // alpha unchanged
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }

  /**
   * Reset camera to fit the current model in the viewport.
   * @param {boolean} adjustHeight - Whether to also adjust responsive height (default: true)
   * @param {boolean} smooth - Animate the distance adjustment instead of snapping (default: false)
   */
  resetCamera(adjustHeight = true, smooth = false) {
    if (!this.camera || !this.controls) return;

    if (adjustHeight) {
      this.adjustResponsiveHeight();
    }

    if (this.model) {
      const box    = new THREE.Box3().setFromObject(this.model);
      const size   = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      const fov         = this.camera.fov * (Math.PI / 180);
      const aspect      = this.camera.aspect;
      const viewerConfig = this.config.viewer_3d;

      const paddingConfig = viewerConfig.camera_padding ?? {
        horizontal: 1.2,
        vertical_landscape: 1.2,
        vertical_portrait:  1.6,
      };

      const horizontalPadding = paddingConfig.horizontal;
      const verticalPadding   = aspect < 1.0
        ? paddingConfig.vertical_portrait
        : paddingConfig.vertical_landscape;

      const distanceForWidth  = Math.abs(
        (size.x * horizontalPadding) / (2 * Math.tan(fov / 2) * aspect),
      );
      const distanceForHeight = Math.abs(
        (size.y * verticalPadding) / (2 * Math.tan(fov / 2)),
      );

      const distance = Math.max(distanceForWidth, distanceForHeight);

      // Always update optimalDistance so animateCameraOrbit uses the new value
      this.optimalDistance = distance;

      this.controls.target.set(center.x, center.y, center.z);

      if (smooth) {
        // Animate to the new distance at the current azimuth/polar — no snap
        const azimuthDeg = (this.currentAzimuthAngle ?? 0) * (180 / Math.PI);
        const polarDeg   = (this.currentPolarAngle   ?? Math.PI / 2) * (180 / Math.PI);
        this.animateCameraOrbit(azimuthDeg, polarDeg, this.currentZoomLevel ?? 1.0);
      } else {
        const azimuth = this.currentAzimuthAngle ?? 0;
        const polar   = this.currentPolarAngle   ?? Math.PI / 2;
        // Apply the active zoom level (e.g. 0.8 for frame_color step)
        const effectiveDist = distance / (this.currentZoomLevel ?? 1.0);

        const camX = center.x + effectiveDist * Math.sin(polar) * Math.sin(azimuth);
        const camY = center.y + effectiveDist * Math.cos(polar);
        const camZ = center.z + effectiveDist * Math.sin(polar) * Math.cos(azimuth);

        this.camera.position.set(camX, camY, camZ);
        this.camera.lookAt(center.x, center.y, center.z);
        this.controls.update();
      }
    } else {
      const viewerConfig = this.config.viewer_3d;
      this.camera.position.set(0, 0, viewerConfig.camera_distance_default);
      this.camera.lookAt(0, 0, 0);
      this.controls.target.set(0, 0, 0);
      this.controls.update();
    }
  }

  refitCamera() {
    if (!this.camera || !this.renderer) return;
    this.camera.aspect = this._nativeWidth / this._nativeHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this._nativeWidth, this._nativeHeight);
    this.resetCamera(false, false);
  }

  /**
   * Clean up the viewer: cancel the animation loop and remove event listeners.
   */
  destroy() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    window.removeEventListener('resize', this.onWindowResize.bind(this));

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    if (this.controls) {
      this.controls.dispose();
    }

    if (this.renderer) {
      this.renderer.dispose();
    }

    if (this.stringArtShader) {
      this.stringArtShader.dispose();
    }

    if (this.model && this.modelBuilder) {
      this.modelBuilder.dispose(this.model);
    }

    this.isInitialized = false;
    console.log('[3D Viewer] Destroyed');
  }
}